<template>
    <products-layout>
        <div class="backoffice-brands">
            <div class="page-header">
                <div class="left">
                    <h2 class="page-title">{{ $page.props.lang.title || "Brands" }}</h2>
                </div>
                <div class="right">
                    <v-btn color="primary" dark @click="newBrandModal = true"><v-icon small class="mr-2">mdi-plus</v-icon>{{ $page.props.lang.newBrand || "New brand" }}</v-btn>
                </div>
            </div>
            <div class="filter-section">
                <v-text-field v-model="form.search" :label="$page.props.lang.filter_search || 'Search'" background-color="#FFFFFF" outlined dense prepend-inner-icon="mdi-magnify" class="table-search"></v-text-field>
            </div>
            <v-dialog v-model="newBrandModal" max-width="500px" content-class="new-brand">
                <v-card>
                    <h4 class="modal-headline">{{ $page.props.lang.newBrand || "New brand" }}</h4>
                    <v-card-text>
                        <v-container>
                            <div class="fields">
                                <div class="grid">
                                    <div class="image-wrap">
                                        <img :src="newBrandPhotoUrl" alt="" width="110" height="110">
                                        <div class="upload">
                                            <v-file-input
                                                    v-model="newBrandPhoto"
                                                    prepend-icon="mdi-camera-outline">
                                            </v-file-input>
                                        </div>
                                    </div>
                                    <v-text-field v-model="newBrand.name" :label="$page.props.lang.name|| 'Name'" :error-messages="responseErrors.name" @focus="responseErrors.name=''"></v-text-field>
                                    <v-textarea v-model="newBrand.description" :label="$page.props.lang.description || 'Description'" :error-messages="responseErrors.description" @focus="responseErrors.description=''"></v-textarea>
                                </div>
                            </div>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn @click="newBrandModal = false" color="regular">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                                <v-btn @click="addBrand" color="primary">{{ $page.props.lang.save || "Save" }}</v-btn>
                            </v-card-actions>
                        </v-container>
                    </v-card-text>
                </v-card>
            </v-dialog>
            <v-snackbar v-model="brandAddMessage.value" :color="brandAddMessage.color">{{ brandAddMessage.text }}</v-snackbar>

            <div class="table-container">
                <v-data-table
                :headers="brandsHeaders"
                :items="brandsData.data"
                item-key="item.id"
                mobile-breakpoint="1200"
                hide-default-footer
                >
                    <template v-slot:item.logo="{ item }">
                        <div class="logo-wrap">
                            <img src="@/img/avatar.jpg" alt="">
                        </div>
                    </template>
                    <template v-slot:item.name="{ item }">
                        <p class="name">{{ item.name }}</p>
                    </template>
                    <template v-slot:item.description="{ item }">
                        <p class="desc">{{ item.description }}</p>
                    </template>
                    <template v-slot:item.action="{ item }">
                        <v-icon @click="openEditBrand(item.id)" class="edit">mdi-pencil</v-icon>
                        <v-icon @click="openDeleteModal(item.id)">mdi-trash-can-outline</v-icon>
                    </template>
                </v-data-table>
                <v-snackbar v-model="editBrandMessage.value" :color="editBrandMessage.color">{{ editBrandMessage.text }}</v-snackbar>

                <confirm-delete :open="deleteModal" @no="deleteModal = false" @yes="deleteBrand"></confirm-delete>

                <pagination :links="brands.links" />
            </div>

            <v-dialog
            v-model="editBrandModal"
            max-width="500px"
            content-class="edit-brand"
            >
                <v-card>
                    <h4 class="modal-headline">{{ $page.props.lang.editBrand || "Edit brand" }}</h4>
                    <v-card-text>
                        <v-container>
                            <div class="fields">
                                <div class="grid">
                                    <div class="image-wrap">
                                        <img :src="newBrandPhotoUrl" alt="" width="110" height="110">
                                        <div class="upload">
                                            <v-file-input
                                                    v-model="newBrandPhoto"
                                                    prepend-icon="mdi-camera-outline">
                                            </v-file-input>
                                        </div>
                                    </div>
                                    <v-text-field v-model="brandForEdit.name" :label="$page.props.lang.table_name|| 'Name'" :error-messages="responseErrors.name" @focus="responseErrors.name=''"></v-text-field>
                                    <v-textarea v-model="brandForEdit.description" :label="$page.props.lang.table_description || 'Description'" :error-messages="responseErrors.description" @focus="responseErrors.description=''"></v-textarea>
                                </div>
                            </div>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn @click="editBrandModal = false" color="regular">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                                <v-btn @click="editBrand" color="primary">{{ $page.props.lang.save || "Save" }}</v-btn>
                            </v-card-actions>
                        </v-container>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </div>
    </products-layout>
</template>

<script>
import ProductsLayout from '@/js/Layouts/ProductsLayout'
import Pagination from "@/js/Components/Pagination"
import ConfirmDelete from '@/js/Components/ConfirmDelete'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
    metaInfo() {
        return {
            title: "Brand"
        }
    },
    components: {
        ProductsLayout,
        Pagination,
        ConfirmDelete
    },
    props: {
        brands: Object,
        filters: Object,
        lang: Object,
        locale: String
    },
    data(){
        return {
            form: {
                search: this.filters.search,
            },
            brandsData: this.brands,
            brandsHeaders: [
                {text: 'Logo', value: 'logo', width: '6rem', sortable: false},
                {text: this.lang.name || 'Name', value: 'name'},
                {text: this.lang.description || 'Description', value: 'description', sortable: false},
                {text: this.lang.action || 'Action', value: 'action', sortable: false}
            ],
            newBrandModal: false,
            newBrandPhoto: null,
            newBrandPhotoUrl: '/images/avatar.jpg',
            newBrand: {
                name: '',
                description: '',
                photo_path: ''
            },
            brandAddMessage: {},
            editBrandModal: false,
            editBrandPhoto: null,
            editBrandPhotoUrl: '/images/avatar.jpg',
            brandForEdit: {},
            editBrandMessage: {},
            responseErrors: {},
            deleteModal: false,
            brandForDeleteId: null
        }
    },
    watch: {
        form: {
            handler: throttle(function() {
                let query = pickBy(this.form)
                this.$inertia.replace("/backoffice/brands?"+(Object.keys(query).length ? "search="+query.search : "remember=forget"))
                .then(() => {
                    this.brandsData = this.brands
                })            
            }, 250),
            deep: true,
        },
        editBrandModal: function(value) {
            if (value===false) {
                this.responseErrors = {}
            }
        }
    },
    methods: {
        openEditBrand(itemId) {
            this.editBrandModal = true
            this.brandForEdit = Object.assign({}, this.brandsData.data.find(item => item.id===itemId))
        },
        editBrand() {
            axios.put('/backoffice/brands/update/'+this.brandForEdit.id, this.brandForEdit)
                .then(response => {
                    if (response.data.status==="OK") {
                        this.editBrandMessage = {
                            value: true,
                            text: response.data.message,
                            color: 'success'
                        }
                        let editedBrandIndex = this.brandsData.data.findIndex(item => item.id===this.brandForEdit.id)
                        this.brandsData.data.splice(editedBrandIndex, 1, this.brandForEdit)
                        this.editBrandModal = false
                    }
                })
                .catch(error => {
                    this.editBrandMessage = {
                        value: true,
                        text: error.response.data.message,
                        color: 'error'
                    }
                    this.responseErrors = error.response.data.errors
                })
        },
        openDeleteModal(itemId) {
            this.deleteModal = true
            this.brandForDeleteId = itemId
        },
        deleteBrand() {
            axios.post('/backoffice/brands/delete', {id: this.brandForDeleteId})
                .then(response => {
                    if(response.data.status==="OK") {
                        this.brandsData.data.splice(this.brandsData.data.findIndex(i => i.id === this.brandForDeleteId), 1)
                        this.editBrandMessage = {
                            value: true,
                            text: response.data.message,
                            color: 'success'
                        }
                    }
                })
                .catch(function (error) {
                
                })
            this.deleteModal = false
        },
        addBrand() {
            axios.post('/backoffice/brands/store', this.newBrand)
                .then(response => {
                    if (response.data.status==="OK") {
                        this.brandAddMessage = {
                            value: true,
                            text: response.data.message,
                            color: 'success'
                        }
                        this.newBrandModal = false
                        this.brandsData.data.unshift(response.data.brand)
                    }
                })
                .catch(error => {
                    this.brandAddMessage = {
                        value: true,
                        text: error.response.data.message,
                        color: 'error'
                    }
                    this.responseErrors = error.response.data.errors
                });
        }
    }
}
</script>
